import { create } from 'zustand';
import { songsApi } from '../api/songs';
import type { SongSummary } from '../types';

/**
 * Generate a stable hash for a song based on title and artist
 * Must match the backend implementation
 */
export function generateSongHash(title: string, artist: string): string {
  const content = `${title.toLowerCase().trim()}|${artist.toLowerCase().trim()}`;

  // Simple but effective hash function
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Convert to base36 for a shorter string
  return Math.abs(hash).toString(36);
}

interface ListenState {
  // Set of listened song hashes
  listenedSongs: Set<string>;

  // Loading state
  isLoading: boolean;

  // Actions
  loadHistory: () => Promise<void>;
  markAsListened: (song: SongSummary) => Promise<void>;
  clearHistory: () => Promise<void>;
  hasListened: (song: SongSummary) => boolean;
}

export const useListenStore = create<ListenState>((set, get) => ({
  listenedSongs: new Set(),
  isLoading: false,

  loadHistory: async () => {
    set({ isLoading: true });
    try {
      const hashes = await songsApi.getListenHistory();
      set({ listenedSongs: new Set(hashes), isLoading: false });
    } catch (error) {
      console.error('Failed to load listen history:', error);
      set({ isLoading: false });
    }
  },

  markAsListened: async (song: SongSummary) => {
    const hash = generateSongHash(song.title, song.artist);

    // Optimistically update local state
    const newSet = new Set(get().listenedSongs);
    newSet.add(hash);
    set({ listenedSongs: newSet });

    // Persist to backend
    try {
      await songsApi.markAsListened(song.id);
    } catch (error) {
      console.error('Failed to mark song as listened:', error);
      // Revert on error
      const revertSet = new Set(get().listenedSongs);
      revertSet.delete(hash);
      set({ listenedSongs: revertSet });
    }
  },

  clearHistory: async () => {
    try {
      await songsApi.clearListenHistory();
      set({ listenedSongs: new Set() });
    } catch (error) {
      console.error('Failed to clear listen history:', error);
      throw error;
    }
  },

  hasListened: (song: SongSummary) => {
    const hash = generateSongHash(song.title, song.artist);
    return get().listenedSongs.has(hash);
  },
}));
